import type React from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './DisclaimerModal.css';

const STORAGE_KEY_PERMANENT = 'theychat-disclaimer-dismissed';
const STORAGE_KEY_SESSION = 'theychat-disclaimer-shown';

const DisclaimerModal: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    // Permanently dismissed — never show
    if (localStorage.getItem(STORAGE_KEY_PERMANENT)) return;
    // Already shown this session — don't show again until next visit
    if (sessionStorage.getItem(STORAGE_KEY_SESSION)) return;

    setIsOpen(true);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleAccept = () => {
    // Permanently dismiss — user has read and accepted
    localStorage.setItem(STORAGE_KEY_PERMANENT, 'true');
    sessionStorage.setItem(STORAGE_KEY_SESSION, 'true');
    setIsOpen(false);
  };

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className="disclaimer-modal-overlay">
      <div
        className="disclaimer-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="disclaimer-modal-title"
      >
        {/* Icon */}
        <div className="disclaimer-modal-icon">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <title>Info Icon</title>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </div>

        {/* Title */}
        <h2 id="disclaimer-modal-title" className="disclaimer-modal-title">
          {t('disclaimer.title', 'Disclaimer')}
        </h2>

        {/* Intro */}
        <p className="disclaimer-modal-intro">
          {t(
            'disclaimer.intro',
            'This platform is an educational, reflective, and cultural bridge designed to help you learn about the lives of the Noble Companions (Sahāba-i Kirām) through the possibilities of modern technology. Before using the application, you are requested to accept the following technical limitations and responsibilities:',
          )}
        </p>

        {/* Content - Bullet points */}
        <div className="disclaimer-modal-content">
          <div className="disclaimer-item">
            <span className="disclaimer-item-label">
              {t('disclaimer.representativeLabel', 'Representative Nature:')}
            </span>
            <span>
              {' '}
              {t(
                'disclaimer.representativeText',
                'The visuals on the platform are not biometric copies of the companions, but rather "artistic digital representations" based on primary sources.',
              )}
            </span>
          </div>

          <div className="disclaimer-item">
            <span className="disclaimer-item-label">
              {t('disclaimer.notRulingLabel', 'Not a Religious Ruling:')}
            </span>
            <span>
              {' '}
              {t(
                'disclaimer.notRulingText',
                'The responses provided do not constitute fatwas or definitive historical documents; they are "educational simulations".',
              )}
            </span>
          </div>

          <div className="disclaimer-item">
            <span className="disclaimer-item-label">
              {t('disclaimer.hallucinationLabel', 'Margin of Error (Hallucination):')}
            </span>
            <span>
              {' '}
              {t(
                'disclaimer.hallucinationText',
                'Generative AI technology may, albeit rarely, confuse historical events or names (AI Hallucination). The system does not guarantee 100% accuracy.',
              )}
            </span>
          </div>

          <div className="disclaimer-item">
            <span className="disclaimer-item-label">
              {t('disclaimer.chronologicalLabel', 'Chronological Inconsistencies:')}
            </span>
            <span>
              {' '}
              {t(
                'disclaimer.chronologicalText',
                'Due to technical limitations, some chronological deviations may occur, such as a Companion appearing to know about the events that happened after their passing.',
              )}
            </span>
          </div>

          <div className="disclaimer-item">
            <span className="disclaimer-item-label">
              {t('disclaimer.modernLabel', 'Modern Concepts:')}
            </span>
            <span>
              {' '}
              {t(
                'disclaimer.modernText',
                'To maintain user experience, the system may recognize and interpret modern concepts (airplanes, internet, etc.); this does not represent historical reality.',
              )}
            </span>
          </div>

          <div className="disclaimer-item">
            <span className="disclaimer-item-label">
              {t('disclaimer.styleLabel', 'Style Limitations:')}
            </span>
            <span>
              {' '}
              {t(
                'disclaimer.styleText',
                'Since artificial intelligence lacks spiritual perception, it may occasionally produce soulless or mechanical expressions that do not fully align with the courtesy of the companions.',
              )}
            </span>
          </div>

          <div className="disclaimer-item">
            <span className="disclaimer-item-label">
              {t('disclaimer.disclaimerLabel', 'Disclaimer:')}
            </span>
            <span>
              {' '}
              {t(
                'disclaimer.disclaimerText',
                'TheyChat team cannot be held responsible for spiritual or legal consequences arising from AI-generated errors or misuse of information.',
              )}
            </span>
          </div>
        </div>

        {/* Checkbox */}
        <label className="disclaimer-checkbox-label">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="disclaimer-checkbox"
          />
          <span className="disclaimer-checkbox-custom" />
          <span className="disclaimer-checkbox-text">
            {t(
              'disclaimer.checkboxText',
              'I acknowledge the above technical limitations, potential errors, and the "representative" nature of the content, and I agree to participate in this digital journey.',
            )}
          </span>
        </label>

        {/* Accept Button */}
        <div className="disclaimer-modal-actions">
          <button
            type="button"
            className="disclaimer-modal-btn-primary"
            onClick={handleAccept}
            disabled={!accepted}
          >
            {t('disclaimer.accept', 'I HAVE READ AND ACCEPT')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DisclaimerModal;
